/**
 * Core parameter builder for job fetching
 * 
 * This module provides the main buildJobIndexParams function
 * that consolidates all parameter building logic with proper
 * priority rules: User Filters > Creator Preferences > Defaults
 */

import type { BuildJobIndexParamsOptions, JobIndexParams } from "./types";
import { extractCreatorContext } from "./contextExtractor";
import { hasValue, pickValue, mergeValue, toCsv } from "./helpers";
import {
  normalizeCountryCode,
  normalizeWorkMode,
  normalizeJobType,
  normalizeNicheValue,
  normalizeSortBy,
} from "./normalizers";

/**
 * Default fallback category when no categories are provided
 */
const DEFAULT_CATEGORY = "software-development";

/**
 * Build job index parameters from creator context and user filters
 * 
 * Applies priority rules:
 * 1. User-applied filters (highest priority)
 * 2. Creator job feed preferences
 * 3. Default fallback values (lowest priority)
 * 
 * All parameters are normalized to API format (CSV strings, lowercase, etc.)
 * 
 * @param options.creator - Creator object (Creator or CreatorProfileForMyLink)
 * @param options.params - User-applied filters
 * @returns Normalized job index parameters ready for API consumption
 * 
 * @example
 * ```typescript
 * // With creator preferences and user filters
 * const params = buildJobIndexParams({
 *   creator: myCreator,
 *   params: { work_mode: ["remote"], salary_min: 100000 }
 * });
 * // Result: {
 * //   work_mode: "remote",
 * //   salary_min: 100000,
 * //   categories: "software-development,design",
 * //   location: "US",
 * //   ...
 * // }
 * 
 * // Without creator (uses defaults)
 * const params = buildJobIndexParams({
 *   params: { categories: ["design"], work_mode: ["remote"] }
 * });
 * // Result: {
 * //   categories: "design",
 * //   work_mode: "remote"
 * // }
 * ```
 */
export function buildJobIndexParams({
  creator,
  params = {},
  merge = false,
}: BuildJobIndexParamsOptions): JobIndexParams {
  // Extract creator context (preferences and categories)
  const { jobFeedPreferences, nicheCategories } = extractCreatorContext(creator);

  // Choose resolution strategy:
  // merge=true  → union arrays (candidate side: creator defaults + candidate filters)
  // merge=false → replace (creator side: user filters override creator preferences)
  const resolve = merge ? mergeValue : pickValue;

  const categoriesValue = resolve(params.categories, nicheCategories);
  const keywordValue = resolve(
    params.niche_keywords,
    jobFeedPreferences?.positiveKeywords
  );
  const locationValue = pickValue(
    params.location,
    jobFeedPreferences?.countries
  );
  const jobTypeValue = resolve(
    params.job_type,
    jobFeedPreferences?.jobTypes
  );
  const workModeValue = resolve(
    params.work_mode,
    jobFeedPreferences?.workModes
  );
  // Scalar values always use pickValue (candidate overrides creator)
  // We treat 0 as undefined for salaries to avoid filtering out jobs with no salary data
  const rawSalaryMin = pickValue(
    params.salary_min,
    jobFeedPreferences?.minSalary
  );
  const salaryMinValue = rawSalaryMin && rawSalaryMin > 0 ? rawSalaryMin : undefined;

  const rawSalaryMax = pickValue(
    params.salary_max,
    jobFeedPreferences?.maxSalary
  );
  const salaryMaxValue = rawSalaryMax && rawSalaryMax > 0 ? rawSalaryMax : undefined;

  // Handle both legacy and new education level fields
  const educationValue = resolve(
    params.education,
    jobFeedPreferences?.educationLevels ||
    (jobFeedPreferences?.educationLevel
      ? [jobFeedPreferences.educationLevel]
      : undefined)
  );

  // Map new fields from backend schema
  const industryValue = resolve(
    params.industry,
    jobFeedPreferences?.industries
  );
  const companyValue = resolve(
    params.company,
    jobFeedPreferences?.companies
  );
  const experienceLevelsValue = resolve(
    params.exp_level,
    jobFeedPreferences?.experienceLevels
  );
  const currencyValue = resolve(
    params.currency || params.currencies,
    jobFeedPreferences?.currencies ||
    (jobFeedPreferences?.currency ? [jobFeedPreferences.currency] : undefined)
  );

  const stateValue = pickValue(params.state, undefined);
  const cityValue = pickValue(params.city, undefined);

  // Build result with normalized values
  const result: JobIndexParams = {
    // Convert arrays/strings to CSV and apply normalization
    categories: toCsv(categoriesValue, normalizeNicheValue),
    niche_keywords: toCsv(keywordValue),
    location: toCsv(locationValue, normalizeCountryCode),
    state: toCsv(stateValue),
    city: toCsv(cityValue),
    job_type: toCsv(jobTypeValue, normalizeJobType),
    work_mode: toCsv(workModeValue, normalizeWorkMode),
    exp_level: toCsv(experienceLevelsValue),
    education: toCsv(educationValue),
    industry: toCsv(industryValue),
    company: toCsv(companyValue),
    currency: toCsv(currencyValue),

    // Numeric values (no normalization needed)
    salary_min: salaryMinValue,
    salary_max: salaryMaxValue,

    // Sort by (normalize to API format)
    sort_by: params.sort_by ? normalizeSortBy(params.sort_by) : undefined,

    // Pagination and deduplication
    limit: params.limit,
    offset: params.offset,
    dedup_by: params.dedup_by,
    posted_after: params.posted_after,
  };

  // Add negative keywords from jobFeedPreferences if available
  const negativeKeywords = jobFeedPreferences?.negativeKeywords;
  if (negativeKeywords?.length) {
    result.negative_keywords = negativeKeywords.join(",");
  }

  // Ensure categories is always provided (default fallback)
  if (!result.categories && !hasValue(categoriesValue)) {
    result.categories = DEFAULT_CATEGORY;
  }

  return result;
}
